'use client';

import { useMemo } from 'react';
import { amountWithCurrencySymbol } from '@/helpers/currency';
import { useCMSCashierBonuses } from '@/modules/bonuses';
import { useCMSPromocodes } from '@/modules/common/hooks/useCMSPromocodes';
import { useDepositStore, useGetCommonStore } from '@/store';
import {
  getCurrentUserBonus,
  getMinBonusDepByBonus,
  getMinBonusDepByPromocode,
} from '../helpers';

export const useDepositBonus = () => {
  const useCommonStore = useGetCommonStore();
  const user = useCommonStore.use.user();
  const currentAmount = useDepositStore.use.currentAmount();

  const { bonuses: cashierBonuses, isLoading: pendingBonuses } =
    useCMSCashierBonuses();
  const { data: promocodes, isLoading: pendingPromocodes } = useCMSPromocodes();

  const depositBonusCode = user?.depositBonusCode;

  const bonus = useMemo(() => {
    if (!depositBonusCode || pendingBonuses || pendingPromocodes) return null;

    return getCurrentUserBonus([...cashierBonuses, ...promocodes], user);
  }, [
    cashierBonuses,
    promocodes,
    depositBonusCode,
    pendingBonuses,
    pendingPromocodes,
    user,
  ]);

  const minBonusDepByCurrency = useMemo(() => {
    if (!user || pendingBonuses) return 0;
    return getMinBonusDepByBonus(cashierBonuses, user);
  }, [cashierBonuses, user, pendingBonuses]);

  const minBonusDepByPromocode = useMemo(() => {
    if (!user || pendingPromocodes) return 0;
    return getMinBonusDepByPromocode(promocodes, user);
  }, [promocodes, user, pendingPromocodes]);

  const minBonusDep = useMemo(
    () => minBonusDepByCurrency || minBonusDepByPromocode,
    [minBonusDepByCurrency, minBonusDepByPromocode]
  );

  const minBonusDepWithCurrency = useMemo(
    () => amountWithCurrencySymbol(user?.currency, minBonusDep),
    [minBonusDep, user?.currency]
  );

  const isDepositBonusCodeBelowMinDep = useMemo(
    () => (minBonusDep ? +currentAmount < minBonusDep : false),
    [currentAmount, minBonusDep]
  );

  return {
    bonus,
    minBonusDepByCurrency,
    minBonusDep,
    minBonusDepWithCurrency,
    isDepositBonusCodeBelowMinDep,
  };
};
